import React, { useState } from "react";
import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, Phone } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { useApp } from "../context/AppContext";
import { phones } from "../i18n/content";
import { DEFAULT_REGION, REGIONS, regionLabel } from "../i18n/regions";
import { useRecaptcha } from "../hooks/useRecaptcha";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export function ApplyModal() {
  const { c, lang, applyOpen, setApplyOpen } = useApp();
  const a = c.apply;
  const [sent, setSent] = useState(false);
  const [methodIndex, setMethodIndex] = useState(0);
  const [fieldError, setFieldError] = useState(null);
  const { isEnabled: isRecaptchaEnabled, getToken } = useRecaptcha();

  const method = a.contactMethods[methodIndex];

  const close = (open) => {
    setApplyOpen(open);
    if (!open) {
      setTimeout(() => {
        setSent(false);
        setFieldError(null);
      }, 300);
    }
  };

  const submitApplication = useMutation({
    mutationFn: (payload) => axios.post(`${BACKEND_URL}/api/applications`, payload),
    onSuccess: () => setSent(true),
    onError: () => toast.error(a.error),
  });

  const submit = async (e) => {
    e.preventDefault();
    setFieldError(null);

    const form = new FormData(e.currentTarget);
    const values = {
      name: form.get("name")?.toString().trim() ?? "",
      contact: form.get("contact")?.toString().trim() ?? "",
      region: form.get("region")?.toString().trim() ?? "",
      amount: form.get("amount")?.toString().trim() ?? "",
      comment: form.get("comment")?.toString().trim() ?? "",
    };

    if (!values.name) return setFieldError(a.errors.name);
    if (!values.contact) return setFieldError(a.errors.contact);
    if (!values.region) return setFieldError(a.errors.region);

    const recaptchaToken = await getToken();

    submitApplication.mutate({
      ...values,
      contact_method: method.value,
      recaptcha_token: recaptchaToken,
    });
  };

  return (
    <Dialog open={applyOpen} onOpenChange={close}>
      <DialogContent
        data-testid="apply-modal"
        className="max-h-[92vh] max-w-lg overflow-y-auto rounded-3xl border-none bg-[#f5f7fa] p-0 shadow-2xl"
      >
        <div className="relative bg-[#002355] px-7 py-6 text-[#f5f7fa]">
          <div className="grain absolute inset-0 opacity-30" />
          <DialogTitle className="relative font-display text-2xl font-bold tracking-tight">
            {a.title}
          </DialogTitle>
          <DialogDescription className="relative mt-1 text-sm text-[#f5f7fa]/75">
            {a.subtitle}
          </DialogDescription>
        </div>

        {sent ? (
          <div data-testid="apply-success" className="flex flex-col items-center gap-4 px-7 py-12 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#002355]/10 text-[#002355]">
              <CheckCircle2 size={40} strokeWidth={1.6} />
            </div>
            <p className="font-display text-lg font-semibold text-[#0a1a2e]">{a.success}</p>
            <button
              data-testid="apply-close-btn"
              onClick={() => close(false)}
              className="mt-2 rounded-full bg-[#002355] px-6 py-2.5 text-sm font-bold text-[#f5f7fa] transition-transform duration-300 hover:scale-105"
            >
              OK
            </button>
          </div>
        ) : (
          <form data-testid="apply-form" onSubmit={submit} noValidate className="grid gap-4 px-7 py-6">
            <Field label={a.fields.name} name="name" testid="apply-name" />

            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-[#3e516a]">
                {a.fields.contactMethod}
              </label>
              <div className="flex flex-wrap gap-2">
                {a.contactMethods.map((m, i) => (
                  <button
                    type="button"
                    key={m.value}
                    data-testid={`apply-method-${i}`}
                    onClick={() => setMethodIndex(i)}
                    className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors duration-300 ${
                      methodIndex === i
                        ? "border-[#002355] bg-[#002355] text-[#f5f7fa]"
                        : "border-[#002355]/20 bg-white text-[#24313f] hover:border-[#002355]/50"
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            <Field
              label={method.fieldLabel}
              name="contact"
              testid="apply-contact"
              type={method.type}
              placeholder={method.placeholder}
            />

            <label className="block">
              <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-[#3e516a]">
                {a.fields.region}
              </span>
              <select
                data-testid="apply-region"
                name="region"
                defaultValue={DEFAULT_REGION}
                className="w-full appearance-none rounded-xl border border-[#002355]/15 bg-white bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 fill=%22none%22 stroke=%22%233e516a%22 stroke-width=%222%22 viewBox=%220 0 24 24%22%3E%3Cpath d=%22m6 9 6 6 6-6%22/%3E%3C/svg%3E')] bg-[length:18px] bg-[right_1rem_center] bg-no-repeat px-4 py-3 pr-11 text-sm text-[#0a1a2e] outline-none transition-colors duration-300 focus:border-[#002355]"
              >
                {REGIONS.map((region) => (
                  <option key={region.value} value={region.value}>
                    {regionLabel(region, lang)}
                  </option>
                ))}
              </select>
            </label>

            <Field label={a.fields.amount} name="amount" testid="apply-amount" placeholder="10 000 000 ₸" />

            <label className="block">
              <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-[#3e516a]">
                {a.fields.comment}
              </span>
              <textarea
                data-testid="apply-comment"
                name="comment"
                rows={3}
                className="w-full resize-none rounded-xl border border-[#002355]/15 bg-white px-4 py-3 text-sm text-[#0a1a2e] outline-none transition-colors duration-300 placeholder:text-[#3e516a]/45 focus:border-[#002355]"
              />
            </label>

            {fieldError && (
              <p
                data-testid="apply-field-error"
                className="rounded-xl border border-[#c0392b]/25 bg-[#c0392b]/8 px-4 py-3 text-sm text-[#c0392b]"
              >
                {fieldError}
              </p>
            )}

            <button
              data-testid="apply-submit-btn"
              type="submit"
              disabled={submitApplication.isPending}
              className="mt-1 rounded-full bg-[#e8a93d] px-6 py-3.5 text-base font-bold text-[#002355] shadow-[0_12px_28px_-12px_rgba(232,169,61,0.9)] transition-transform duration-300 hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100"
            >
              {submitApplication.isPending ? a.submitting : a.submit}
            </button>

            <div className="flex items-center justify-center gap-2 text-xs text-[#3e516a]">
              <Phone size={13} /> {phones[0]}
            </div>
            <p className="text-center text-[11px] text-[#3e516a]/70">{a.note}</p>

            {isRecaptchaEnabled && (
              <p className="text-center text-[10px] leading-relaxed text-[#3e516a]/60">
                {a.recaptcha.prefix}
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-[#002355]"
                >
                  {a.recaptcha.privacy}
                </a>
                {a.recaptcha.and}
                <a
                  href="https://policies.google.com/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-[#002355]"
                >
                  {a.recaptcha.terms}
                </a>
                {a.recaptcha.suffix}
              </p>
            )}
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, name, testid, type = "text", placeholder }) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-[#3e516a]">
        {label}
      </span>
      <input
        data-testid={testid}
        name={name}
        type={type}
        placeholder={placeholder}
        className="w-full rounded-xl border border-[#002355]/15 bg-white px-4 py-3 text-sm text-[#0a1a2e] outline-none transition-colors duration-300 placeholder:text-[#3e516a]/45 focus:border-[#002355]"
      />
    </label>
  );
}
